import useAuth from 'hooks/useAuth.ts';
import { useHasPermissions } from 'utils/permissionUtil';

/**
 * Centralised employee read/update permission derivation. The employee page, its
 * cards and the employee form need the same "can I read my own personal data" /
 * role-visibility logic; keep them in sync by going through this hook.
 *
 * `employeeId` is the id of the *viewed* employee (from URL params).
 * Self-access checks compare it against the authenticated user id.
 */
export const useEmployeePermissions = (employeeId: string | number | undefined) => {
    const { user } = useAuth();
    const userIdString = user?.id?.toString() ?? '';
    const employeeIdString = employeeId !== undefined ? String(employeeId) : '';
    const isSelf = userIdString !== '' && userIdString === employeeIdString;

    const hasReadPersonalDataPermission = useHasPermissions(['READ.EMPLOYEE_PERSONAL.ALL']);
    const hasReadPersonalIdCodePermission = useHasPermissions(['READ.EMPLOYEE_PERSONAL.ID_CODE']);
    const canUpdatePersonalData = useHasPermissions(['UPDATE.EMPLOYEE_PERSONAL.ALL']);
    const hasUpdateEmployees = useHasPermissions(['UPDATE.EMPLOYEES']);
    // OR — at least one of the two is needed to enter the edit form at all.
    const hasEmployeeUpdatePermission = useHasPermissions(['UPDATE.EMPLOYEE_PERSONAL.ALL', 'UPDATE.EMPLOYEES']);

    const hasUpdateEmployeeBasicRolesPermission = useHasPermissions(['UPDATE.EMPLOYEES.BASIC_ROLES']);
    const hasUpdateEmployeeAllRolesPermission = useHasPermissions(['UPDATE.EMPLOYEES.ALL_ROLES']);
    const hasReadEmployeeBasicRolesPermission = useHasPermissions(['READ.EMPLOYEES.BASIC_ROLES']);
    const hasReadEmployeeAllRolesPermission = useHasPermissions(['READ.EMPLOYEES.ALL_ROLES']);
    const hasUpdatePartnerPeerGroupPermission = useHasPermissions(['UPDATE.PARTNER_PEER_GROUP']);
    const hasReadPartnerPeerGroupMyOwnPermission = useHasPermissions(['READ.PARTNER_PEER_GROUP.MY_OWN']);
    const canEditImpersonation = useHasPermissions(['UPDATE.EMPLOYEES.EDIT_IMPERSONATION']);
    // Gives the "Show" link after an id code read as asterisks; the backend sends them while the firm obfuscates it.
    const canShowPersonalIdCode = useHasPermissions(['READ.EMPLOYEES.UNHIDE_PERSONAL_ID_CODE']);

    const canReadAllPersonalData = isSelf || hasReadPersonalDataPermission;
    const canReadPersonalData = canReadAllPersonalData || hasReadPersonalIdCodePermission;

    const hasReadRolePermission = hasReadEmployeeBasicRolesPermission || hasReadEmployeeAllRolesPermission;
    const hasUpdateRolesPermission = hasUpdateEmployeeBasicRolesPermission || hasUpdateEmployeeAllRolesPermission;
    // Administrative roles can only be edited with UPDATE.EMPLOYEES.ALL_ROLES.
    const canUpdateAdministrativeRoles = hasUpdateEmployeeAllRolesPermission;
    const canSeeAllRoles = hasReadEmployeeAllRolesPermission;
    // Mirrors the backend EmployeeSecurity.hasReadAccessToPartnerPeerGroup: full update access, or
    // read-my-own on your own record. Used to hide the field (and its history) entirely otherwise.
    const canReadPartnerPeerGroup =
        hasUpdatePartnerPeerGroupPermission || (hasReadPartnerPeerGroupMyOwnPermission && isSelf);

    return {
        isSelf,
        canReadAllPersonalData,
        canReadPersonalData,
        canUpdatePersonalData,
        hasUpdateEmployees,
        hasEmployeeUpdatePermission,
        hasReadRolePermission,
        hasUpdateRolesPermission,
        canUpdateAdministrativeRoles,
        canSeeAllRoles,
        hasUpdatePartnerPeerGroupPermission,
        canReadPartnerPeerGroup,
        canEditImpersonation,
        canShowPersonalIdCode,
    };
};
